import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LAST_UPDATED = "July 3, 2026";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold font-headline text-foreground mb-3">{title}</h2>
    <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
  </section>
);

export default function TermsPage() {
  return (
    <>
      <Helmet>
        <title>Terms of Service — PostWriter</title>
        <meta name="description" content="The terms that govern your use of PostWriter." />
        <meta property="og:url" content="https://postwriter.tech/terms" />
        <meta property="og:title" content="Terms of Service — PostWriter" />
      </Helmet>
      <main className="min-h-screen bg-background">
        <header className="border-b border-border">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="PostWriter" className="w-7 h-7" />
              <span className="text-lg font-bold font-headline text-foreground">PostWriter</span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-6 py-12">
          <h1 className="text-3xl font-bold font-headline text-foreground mb-2">
            Terms of Service
          </h1>
          <p className="text-sm text-muted-foreground mb-10 font-mono">
            Last updated: {LAST_UPDATED}
          </p>

          <Section title="1. Agreement">
            <p>
              These Terms of Service (&ldquo;Terms&rdquo;) are an agreement between you and
              PostWriter, operated by Amrit Singh (&ldquo;PostWriter&rdquo;, &ldquo;we&rdquo;,
              &ldquo;us&rdquo;). By creating an account or using{" "}
              <a href="https://postwriter.tech" className="text-primary hover:underline">
                postwriter.tech
              </a>
              , you agree to these Terms. If you do not agree, do not use the service.
            </p>
          </Section>

          <Section title="2. The service">
            <p>
              PostWriter generates LinkedIn post drafts using AI, informed by your preferences and
              publicly available sources. You are responsible for reviewing, editing, and deciding
              whether to publish any generated content. PostWriter is an independent product and
              is not affiliated with, endorsed by, or sponsored by LinkedIn Corporation.
            </p>
          </Section>

          <Section title="3. Accounts">
            <p>
              You must be at least 18 years old and provide accurate information. Sign-in is via
              Google; you are responsible for activity under your account. One account per person.
              You may not share, sell, or transfer your account.
            </p>
          </Section>

          <Section title="4. Plans, billing, and refunds">
            <p>
              PostWriter offers a 7-day free trial (no credit card required) and paid
              subscriptions (Pro and Ultra), billed monthly through Lemon Squeezy, our merchant of
              record. Prices are shown at checkout; applicable taxes are handled by Lemon Squeezy.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Subscriptions renew automatically each month until cancelled.</li>
              <li>
                You can cancel anytime from Settings &rarr; Billing; you keep access until the end
                of the paid period.
              </li>
              <li>
                If PostWriter isn&rsquo;t working for you, email us within 14 days of a charge and
                we&rsquo;ll issue a refund for that charge.
              </li>
              <li>We may change prices with at least 30 days&rsquo; notice before renewal.</li>
            </ul>
          </Section>

          <Section title="5. Content ownership">
            <p>
              <strong className="text-foreground">Your inputs are yours.</strong> You retain all
              rights to the information you provide (experiences, preferences, edits).
            </p>
            <p>
              <strong className="text-foreground">Generated posts are yours.</strong> To the
              extent we hold any rights in content generated for you, we assign them to you. You
              may use generated posts anywhere, with or without attribution.
            </p>
            <p>
              <strong className="text-foreground">You are responsible for what you publish.</strong>{" "}
              AI-generated content can contain errors or inaccuracies. Verify facts, statistics,
              and claims before posting. Content you publish on LinkedIn is subject to
              LinkedIn&rsquo;s own terms and policies.
            </p>
          </Section>

          <Section title="6. Acceptable use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Use the service to generate spam, misleading, defamatory, or unlawful content</li>
              <li>Impersonate others or misrepresent generated content as another person&rsquo;s work</li>
              <li>Reverse engineer, scrape, resell, or abuse the service or its APIs</li>
              <li>Circumvent usage limits, trials, or billing</li>
            </ul>
            <p>We may suspend or terminate accounts that violate these Terms.</p>
          </Section>

          <Section title="7. Availability and changes">
            <p>
              We work to keep PostWriter available and reliable, but the service is provided
              &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranties of any kind.
              We may add, change, or remove features. If we discontinue the service entirely, we
              will give paying subscribers reasonable notice and a pro-rata refund of any unused
              period.
            </p>
          </Section>

          <Section title="8. Limitation of liability">
            <p>
              To the maximum extent permitted by law, PostWriter&rsquo;s total liability for any
              claim arising from these Terms or the service is limited to the amount you paid us
              in the 12 months before the claim. We are not liable for indirect or consequential
              damages, lost profits, or consequences of content you choose to publish, including
              any action taken by LinkedIn against your account.
            </p>
          </Section>

          <Section title="9. Termination">
            <p>
              You may stop using PostWriter and delete your account at any time by emailing{" "}
              <a href="mailto:amrit@postwriter.tech" className="text-primary hover:underline">
                amrit@postwriter.tech
              </a>
              . We may suspend or terminate access for violation of these Terms, with notice where
              practical. Sections 5, 8, and 10 survive termination.
            </p>
          </Section>

          <Section title="10. Governing law">
            <p>
              These Terms are governed by the laws of India. Any disputes will be subject to the
              exclusive jurisdiction of the courts of India. We&rsquo;ll always try to resolve
              issues informally first — just email us.
            </p>
          </Section>

          <Section title="11. Changes to these Terms">
            <p>
              We may update these Terms as the product evolves. We will post updates here and
              update the date above; material changes will be announced by email or in the app.
              Continued use after changes take effect constitutes acceptance.
            </p>
          </Section>

          <Section title="12. Contact">
            <p>
              Questions about these Terms:{" "}
              <a href="mailto:amrit@postwriter.tech" className="text-primary hover:underline">
                amrit@postwriter.tech
              </a>
              .
            </p>
          </Section>
        </div>
      </main>
    </>
  );
}
